import type {
  CreatePaymentProviderRequest,
  CreatePaymentProviderResponse,
  ListPaymentProvidersResponse,
  RevealPaymentProviderKeyResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers + React Query hooks for `/v1/ops/payment-providers`
 * (Epic #84 PR-P16 follow-up to P15).
 *
 *   - `GET /v1/ops/payment-providers`              — list all rows, active + retired
 *   - `POST /v1/ops/payment-providers`             — append new row (+ optional retire)
 *   - `GET /v1/ops/payment-providers/:id/reveal-key` — plaintext + audit
 *
 * Query key convention:
 *   - `['payment-providers']` — list entity; `useCreatePaymentProvider`
 *     invalidates it on success so the newly-created row + the retired
 *     row (when `make_active=true`) appear in the list without a page
 *     refresh.
 *
 * Reveal key is modeled as a mutation (not a query) even though it's a
 * GET on the wire: each call writes `ops_audit` server-side so we NEVER
 * want it to run automatically on mount / focus / interval. A mutation's
 * explicit-call semantics are the right shape.
 *
 * Spoof-resistance: the client never includes `updated_by` in the body.
 * Platform-api fills it from the ops-session header — matching the
 * pricing + gift-card precedent.
 */

const BASE_PATH = '/v1/ops/payment-providers';

export const PAYMENT_PROVIDERS_QUERY_KEY = ['payment-providers'] as const;

// ─── List ───────────────────────────────────────────────────────────────

export async function getPaymentProviders(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListPaymentProvidersResponse> {
  return opsApiRequest<ListPaymentProvidersResponse>(
    BASE_PATH,
    { signal },
    fetchImpl,
  );
}

export function usePaymentProviders(): UseQueryResult<ListPaymentProvidersResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: PAYMENT_PROVIDERS_QUERY_KEY,
    queryFn: ({ signal }) => getPaymentProviders(fetchImpl, signal),
  });
}

// ─── Create ─────────────────────────────────────────────────────────────

export async function createPaymentProvider(
  body: CreatePaymentProviderRequest,
  fetchImpl: typeof fetch,
): Promise<CreatePaymentProviderResponse> {
  return opsApiRequest<CreatePaymentProviderResponse>(
    BASE_PATH,
    { method: 'POST', body },
    fetchImpl,
  );
}

export function useCreatePaymentProvider(): UseMutationResult<
  CreatePaymentProviderResponse,
  Error,
  CreatePaymentProviderRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createPaymentProvider(body, fetchImpl),
    onSuccess: () => {
      // Invalidate the list so the UI reflects both the new row and the
      // retired row (when make_active=true flips the previous active).
      // Server returns both in the response, but we still refetch — the
      // list view sorts by (provider, updated_at DESC) and we want the
      // canonical ordering, not append-the-response-row.
      void qc.invalidateQueries({ queryKey: PAYMENT_PROVIDERS_QUERY_KEY });
    },
  });
}

// ─── Reveal key (mutation shape; GET on wire) ───────────────────────────

export async function revealPaymentProviderKey(
  id: string,
  fetchImpl: typeof fetch,
): Promise<RevealPaymentProviderKeyResponse> {
  return opsApiRequest<RevealPaymentProviderKeyResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/reveal-key`,
    { method: 'GET' },
    fetchImpl,
  );
}

export function useRevealPaymentProviderKey(): UseMutationResult<
  RevealPaymentProviderKeyResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  return useMutation({
    // Intentionally no query invalidation — a reveal is a read that
    // writes an audit row, but the list view's masked display is
    // unchanged by it.
    mutationFn: (id) => revealPaymentProviderKey(id, fetchImpl),
  });
}
